import React from 'react';
import { SUPPORT_EMAIL } from '../constants/companyInfo';
import SeoHead from '../components/SeoHead';
import { PAGE_SEO } from '../constants/seo';
import './Page.css';

const OFFICE_ADDRESS = 'B-128, B Block, Sector 2, Noida, Uttar Pradesh – 201301';
const WEBSITE_URL = 'https://unaffectedhealthcare.in/';

const Terms = () => {
  return (
    <div className="page">
      <SeoHead {...PAGE_SEO.terms} />
      <section className="page-header">
        <div className="container">
          <h1>Terms &amp; Conditions</h1>
          <p className="page-subtitle">Last updated: 9 September 2026</p>
        </div>
      </section>

      <section className="page-content">
        <div className="container">
          <div className="legal-content">
            <div className="content-section">
              <h2>Welcome to Unaffected Healthcare</h2>
              <p>
                These Terms &amp; Conditions (&quot;Terms&quot;, &quot;Terms and Conditions&quot;) govern your
                access to and use of the Unaffected Healthcare website, platform, portal and
                healthcare-related services made available through them (collectively, the
                &quot;Platform&quot;).
              </p>
              <p>The Platform is operated by:</p>
              <p>
                <strong>Unaffected Healthcare LLP</strong>
                <br />
                Office Address: {OFFICE_ADDRESS}
                <br />
                Website:{' '}
                <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                  {WEBSITE_URL}
                </a>
              </p>
              <p>
                In these Terms, &quot;Unaffected Healthcare&quot;, &quot;we&quot;, &quot;us&quot; and &quot;our&quot;
                refer to Unaffected Healthcare LLP.
              </p>
              <p>
                &quot;User&quot;, &quot;you&quot; and &quot;your&quot; refer to an employee, eligible family
                member/dependent, visitor or other individual accessing or using the Platform.
              </p>
              <p>
                &quot;Organization&quot; means a company, employer, institution or other entity that
                subscribes to, partners with or otherwise provides access to Unaffected
                Healthcare&apos;s services for its eligible employees.
              </p>
              <p>
                By accessing or using the Platform, you acknowledge that you have read,
                understood and agreed to these Terms.
              </p>
              <p>If you do not agree with these Terms, you should not use the Platform.</p>
            </div>

            <div className="content-section">
              <h2>1. About Unaffected Healthcare</h2>
              <p>
                Unaffected Healthcare is a healthcare technology and employee healthcare platform
                that enables organizations to provide healthcare access and benefits to their
                eligible employees and, where applicable, their family members or dependents.
              </p>
              <p>
                Depending on the applicable organization plan, location and service availability,
                the Platform may facilitate access to services including:
              </p>
              <ul>
                <li>Teleconsultation</li>
                <li>General physician consultations</li>
                <li>Specialist consultations</li>
                <li>Diagnostic tests</li>
                <li>Health check-ups</li>
                <li>Pharmacy and medicine-related services</li>
                <li>Dental consultations and services</li>
                <li>Mental wellness services</li>
                <li>Preventive healthcare services</li>
                <li>Wellness programs</li>
                <li>Healthcare discounts and benefits</li>
                <li>Other healthcare-related services introduced from time to time</li>
              </ul>
              <p>
                Unaffected Healthcare may provide certain services directly and may also facilitate
                services through independent third-party healthcare providers and partners.
              </p>
              <p>
                The services available to a particular User will depend on the plan selected by the
                Organization, User eligibility, location, healthcare partner availability and other
                applicable conditions.
              </p>
            </div>

            <div className="content-section">
              <h2>2. Organization-Sponsored Healthcare Model</h2>
              <p>
                Unaffected Healthcare primarily operates on a B2B, organization-sponsored model.
              </p>
              <p>Under this model:</p>
              <ol>
                <li>
                  An Organization subscribes to or enters into an arrangement with Unaffected
                  Healthcare.
                </li>
                <li>
                  The Organization identifies eligible employees who are entitled to access the
                  applicable healthcare program.
                </li>
                <li>
                  The Organization may provide relevant employee information to Unaffected
                  Healthcare for account creation and administration.
                </li>
                <li>Unaffected Healthcare creates or enables eligible employee accounts.</li>
                <li>
                  Employees access the Platform using their registered official email address and
                  applicable authentication method.
                </li>
                <li>Employees may complete or update their profiles.</li>
                <li>
                  Where permitted under the applicable plan, employees may add eligible family
                  members or dependents.
                </li>
                <li>
                  Employees may access the healthcare services and benefits included in their
                  Organization&apos;s applicable plan.
                </li>
              </ol>
              <p>
                Unless expressly stated otherwise, employees do not independently purchase the
                Organization-sponsored subscription from Unaffected Healthcare.
              </p>
              <p>
                The commercial relationship between the Organization and Unaffected Healthcare may
                be governed by a separate proposal, order form, agreement, subscription agreement,
                invoice or other commercial document.
              </p>
              <p>
                Where such a separate agreement exists, its specific commercial terms may prevail
                over these Terms to the extent of any conflict.
              </p>
            </div>

            <div className="content-section">
              <h2>3. Eligibility</h2>
              <p>The Platform may be accessed by:</p>
              <ul>
                <li>Eligible employees of participating Organizations;</li>
                <li>
                  Eligible family members or dependents, where permitted under the applicable plan;
                  and
                </li>
                <li>Other users who are expressly permitted to use particular services.</li>
              </ul>
              <p>
                Unless a service specifically permits otherwise, Users must be at least 18 years of
                age to independently use the Platform.
              </p>
              <p>
                Where a User accesses services for a minor or dependent, the User represents that
                they are legally authorised to do so.
              </p>
              <p>
                Unaffected Healthcare may verify a User&apos;s eligibility with the sponsoring
                Organization before providing certain services.
              </p>
            </div>

            <div className="content-section">
              <h2>4. Account Creation and Registration</h2>
              <p>
                Employee accounts may be created using information provided to Unaffected Healthcare
                by the sponsoring Organization.
              </p>
              <p>The Organization may provide information such as the employee&apos;s:</p>
              <ul>
                <li>Name;</li>
                <li>Official email address;</li>
                <li>Employee identification information;</li>
                <li>Contact information; and</li>
                <li>Other information reasonably required to administer the healthcare program.</li>
              </ul>
              <p>
                The employee may access their account using the official email address registered
                with Unaffected Healthcare by the Organization and the authentication mechanism made
                available by Unaffected Healthcare.
              </p>
              <p>
                After logging in, the employee may be required to complete, verify or update their
                profile.
              </p>
              <p>
                The employee is responsible for ensuring that information entered into their profile
                is accurate, complete and up to date.
              </p>
              <p>
                Unaffected Healthcare may rely on information received from the Organization for
                determining employee eligibility.
              </p>
            </div>

            <div className="content-section">
              <h2>5. Account Security</h2>
              <p>
                Users are responsible for maintaining the confidentiality and security of their
                account and authentication information.
              </p>
              <p>You must not:</p>
              <ul>
                <li>Share your login credentials with another person;</li>
                <li>Allow another person to use your account;</li>
                <li>Attempt to access another person&apos;s account;</li>
                <li>Use another employee&apos;s official email address;</li>
                <li>Provide false or misleading information; or</li>
                <li>Attempt to bypass authentication or security mechanisms.</li>
              </ul>
              <p>
                If you believe that your account has been accessed without authorization, you should
                notify Unaffected Healthcare as soon as reasonably possible.
              </p>
              <p>
                Unaffected Healthcare shall not be responsible for unauthorized access resulting from
                the User&apos;s failure to maintain appropriate account security, except to the
                extent required under applicable law.
              </p>
            </div>

            <div className="content-section">
              <h2>6. Employee Family Members and Dependents</h2>
              <p>
                Where permitted by the Organization&apos;s plan, an eligible employee may add family
                members or dependents to their account.
              </p>
              <p>
                The number and type of family members or dependents that may be added may vary
                depending on the applicable Organization plan.
              </p>
              <p>
                The employee is responsible for providing accurate information regarding family
                members or dependents.
              </p>
              <p>
                By adding another individual to the Platform, the employee represents that they are
                authorised to provide the information and request services on that person&apos;s
                behalf where such authorization is required.
              </p>
              <p>Unaffected Healthcare may request additional information to verify eligibility.</p>
            </div>

            <div className="content-section">
              <h2>7. Organization Information and Employee Data</h2>
              <p>
                The sponsoring Organization may provide employee information to Unaffected Healthcare
                for purposes including:
              </p>
              <ul>
                <li>Creating and administering employee accounts;</li>
                <li>Verifying eligibility;</li>
                <li>Providing healthcare benefits;</li>
                <li>Managing employee and dependent access;</li>
                <li>Communicating account-related information;</li>
                <li>Providing customer support;</li>
                <li>Coordinating healthcare services;</li>
                <li>Administering the Organization&apos;s healthcare program;</li>
                <li>Generating applicable usage or program-level information; and</li>
                <li>Complying with contractual, legal and regulatory requirements.</li>
              </ul>
              <p>
                Unaffected Healthcare will process personal data in accordance with applicable law,
                contractual obligations and its Privacy Policy.
              </p>
              <p>
                Unaffected Healthcare may also collect additional information directly from Users
                when they use the Platform or request healthcare services.
              </p>
            </div>

            <div className="content-section">
              <h2>8. Privacy of Healthcare Information</h2>
              <p>
                Unaffected Healthcare recognizes that healthcare information is sensitive and
                requires appropriate protection.
              </p>
              <p>
                Information relating to an individual&apos;s consultations, prescriptions, diagnostic
                reports, healthcare requests or other health-related activities will be handled in
                accordance with applicable law and Unaffected Healthcare&apos;s Privacy Policy.
              </p>
              <p>
                An employee&apos;s participation in an Organization-sponsored healthcare program does
                not, by itself, mean that the employee&apos;s individual medical information will
                automatically be disclosed to their employer.
              </p>
              <p>
                Information may be shared with the Organization where permitted by applicable law,
                required for legitimate program administration, expressly agreed, aggregated or
                otherwise handled in accordance with applicable privacy requirements.
              </p>
              <p>
                Unaffected Healthcare will take reasonable measures to protect personal and
                healthcare-related information against unauthorized access, use, disclosure,
                alteration or loss, subject to applicable law and the nature of the Platform.
              </p>
            </div>

            <div className="content-section">
              <h2>9. Healthcare Providers and Third-Party Services</h2>
              <p>
                Certain services available through the Platform may be delivered by independent
                third-party healthcare providers, including:
              </p>
              <ul>
                <li>Registered Medical Practitioners;</li>
                <li>Dentists;</li>
                <li>Psychologists or counsellors;</li>
                <li>Diagnostic laboratories;</li>
                <li>Pharmacies;</li>
                <li>Hospitals;</li>
                <li>Clinics;</li>
                <li>Healthcare professionals; and</li>
                <li>Other healthcare service providers.</li>
              </ul>
              <p>
                Such healthcare providers may operate independently and may have their own
                professional obligations, policies and terms.
              </p>
              <p>
                Where a healthcare service is provided by a third-party healthcare provider, the
                provider is responsible for the professional service it delivers.
              </p>
              <p>
                Unaffected Healthcare does not replace the professional judgment of a qualified
                healthcare provider.
              </p>
            </div>

            <div className="content-section">
              <h2>10. Teleconsultation Services</h2>
              <p>
                Where teleconsultation is available, Unaffected Healthcare may facilitate remote
                interaction between Users and healthcare professionals.
              </p>
              <p>
                Teleconsultation services are intended to be provided in accordance with applicable
                laws, regulations and professional guidelines, including applicable telemedicine
                requirements.
              </p>
              <p>
                India&apos;s Telemedicine Practice Guidelines establish requirements concerning
                matters including physician-patient relationships, patient identification, consent,
                consultation, records, privacy and professional responsibilities.
              </p>
              <p>
                The healthcare professional conducting a consultation is responsible for exercising
                appropriate professional judgment.
              </p>
              <p>A healthcare professional may determine that:</p>
              <ul>
                <li>A physical examination is necessary;</li>
                <li>Additional diagnostic tests are required;</li>
                <li>An in-person consultation is required;</li>
                <li>
                  The medical issue cannot appropriately be assessed through teleconsultation; or
                </li>
                <li>Emergency medical treatment is required.</li>
              </ul>
              <p>
                Unaffected Healthcare does not guarantee that every medical condition can be
                diagnosed or treated through teleconsultation.
              </p>
            </div>

            <div className="content-section">
              <h2>11. Emergency Medical Conditions</h2>
              <p>The Platform is not an emergency medical service.</p>
              <p>You should not use the Platform as a substitute for emergency medical care.</p>
              <p>
                If you are experiencing a medical emergency, including but not limited to severe
                chest pain, difficulty breathing, severe bleeding, loss of consciousness, stroke
                symptoms, serious injury or any other potentially life-threatening condition, you
                should immediately contact appropriate emergency services or visit the nearest
                emergency medical facility.
              </p>
              <p>
                Unaffected Healthcare does not guarantee immediate medical assistance, emergency
                response or emergency intervention through the Platform.
              </p>
            </div>

            <div className="content-section">
              <h2>12. Medical Advice and Treatment</h2>
              <p>
                Medical advice, diagnosis, prescriptions and treatment recommendations are the
                responsibility of the healthcare professional providing the relevant service.
              </p>
              <p>
                Information published on the Platform, including health articles, blogs, educational
                materials, videos, health tips and general information, is provided for general
                informational purposes.
              </p>
              <p>Such information is not intended to constitute:</p>
              <ul>
                <li>A medical diagnosis;</li>
                <li>A prescription;</li>
                <li>Individualized medical advice;</li>
                <li>Emergency medical advice; or</li>
                <li>A substitute for consultation with a qualified healthcare professional.</li>
              </ul>
              <p>
                Users should seek appropriate professional medical advice before making decisions
                regarding diagnosis, treatment, medication or other healthcare matters.
              </p>
            </div>

            <div className="content-section">
              <h2>13. Diagnostic Services</h2>
              <p>
                Unaffected Healthcare may facilitate diagnostic tests through third-party
                laboratories and diagnostic service providers.
              </p>
              <p>Availability of diagnostic services may depend on:</p>
              <ul>
                <li>Location;</li>
                <li>Test availability;</li>
                <li>Laboratory capacity;</li>
                <li>Sample collection availability;</li>
                <li>Appointment slots;</li>
                <li>Partner arrangements; and</li>
                <li>Other operational factors.</li>
              </ul>
              <p>Diagnostic reports are generated by the relevant diagnostic service provider.</p>
              <p>
                Unaffected Healthcare does not independently guarantee the accuracy of a diagnostic
                result generated by a third-party laboratory.
              </p>
              <p>
                Users should consult a qualified healthcare professional for interpretation of
                diagnostic results where appropriate.
              </p>
            </div>

            <div className="content-section">
              <h2>14. Pharmacy and Medicine Services</h2>
              <p>
                Where pharmacy or medicine-related services are available, orders may be fulfilled by
                third-party pharmacies or authorized pharmacy partners.
              </p>
              <p>
                Medicine availability, pricing, delivery timelines and prescription requirements may
                vary.
              </p>
              <p>
                Prescription medicines will be supplied only in accordance with applicable laws and
                prescription requirements.
              </p>
              <p>Unaffected Healthcare does not encourage self-medication.</p>
              <p>
                The decision to prescribe a medicine remains the responsibility of the treating
                healthcare professional.
              </p>
              <p>
                Unaffected Healthcare does not guarantee the availability of a particular medicine at
                all times.
              </p>
            </div>

            <div className="content-section">
              <h2>15. Dental Services</h2>
              <p>
                Dental services may be provided through independent dentists, clinics or dental
                partners.
              </p>
              <p>
                The availability of dental consultations, dental procedures, diagnostics, discounts
                and other dental benefits depends on the applicable Organization plan and healthcare
                partner.
              </p>
              <p>
                Clinical decisions and treatment recommendations remain the responsibility of the
                relevant dental professional.
              </p>
            </div>

            <div className="content-section">
              <h2>16. Mental Wellness Services</h2>
              <p>
                Where mental wellness, counselling or psychological services are available, such
                services may be provided by qualified professionals or third-party partners.
              </p>
              <p>
                These services are intended to support general mental wellbeing and are not a
                substitute for emergency psychiatric or medical treatment.
              </p>
              <p>
                If a User is experiencing an immediate mental health emergency or believes that they
                or another person is in immediate danger, appropriate emergency medical assistance
                should be sought immediately.
              </p>
            </div>

            <div className="content-section">
              <h2>17. Healthcare Plans and Benefits</h2>
              <p>Healthcare plans may differ between Organizations.</p>
              <p>Depending on the applicable plan, benefits may include:</p>
              <ul>
                <li>Teleconsultation;</li>
                <li>Diagnostic services or discounts;</li>
                <li>Pharmacy services or discounts;</li>
                <li>Dental services;</li>
                <li>Mental wellness services;</li>
                <li>Preventive healthcare;</li>
                <li>Health check-ups;</li>
                <li>Family member benefits;</li>
                <li>Wellness services; and</li>
                <li>Other healthcare-related services.</li>
              </ul>
              <p>
                Unaffected Healthcare does not represent that every benefit will be available to
                every User.
              </p>
              <p>
                The benefits available to a User are determined by the Organization&apos;s selected
                plan and the applicable eligibility conditions.
              </p>
            </div>

            <div className="content-section">
              <h2>18. Complimentary Access</h2>
              <p>
                Unaffected Healthcare may provide complimentary access to selected Organizations,
                employees or eligible users under:
              </p>
              <ul>
                <li>Promotional programs;</li>
                <li>Pilot programs;</li>
                <li>Partnership arrangements;</li>
                <li>Introductory programs; or</li>
                <li>Other business arrangements.</li>
              </ul>
              <p>
                &quot;Complimentary access&quot; does not necessarily mean that every healthcare
                service available through the Platform is completely free.
              </p>
              <p>
                Certain services may involve additional charges, partner charges, consultation fees,
                diagnostic charges, medicine costs, procedures or other costs depending on the
                applicable program.
              </p>
              <p>
                The applicable benefits, limitations and duration of complimentary access will be
                communicated to the relevant Organization or User.
              </p>
            </div>

            <div className="content-section">
              <h2>19. Subscription, Pricing and Payments</h2>
              <p>
                Unaffected Healthcare&apos;s primary subscription model is organization-sponsored.
              </p>
              <p>
                The Organization may purchase or subscribe to a healthcare program for its eligible
                employees.
              </p>
              <p>
                The commercial terms between Unaffected Healthcare and the Organization may include:
              </p>
              <ul>
                <li>Subscription fees;</li>
                <li>Number of eligible employees;</li>
                <li>Subscription period;</li>
                <li>Applicable healthcare benefits;</li>
                <li>Service limits;</li>
                <li>Family/dependent eligibility;</li>
                <li>Applicable taxes;</li>
                <li>Additional charges, where applicable; and</li>
                <li>Other agreed commercial terms.</li>
              </ul>
              <p>
                The Organization&apos;s payment obligations will be governed by the applicable
                commercial agreement, proposal, order form, invoice, subscription agreement or other
                written arrangement.
              </p>
              <p>
                Unless expressly stated otherwise, an employee is not responsible for paying the
                Organization&apos;s subscription fee.
              </p>
              <p>
                If a particular healthcare service is not included in the Organization&apos;s plan
                and carries an additional charge, the applicable charge will be communicated to the
                User or Organization, as appropriate, before the charge is incurred, wherever
                reasonably practicable.
              </p>
              <p>
                Unaffected Healthcare may introduce individual or direct-to-consumer services in the
                future. Such services, if introduced, may be governed by separate terms.
              </p>
            </div>

            <div className="content-section">
              <h2>20. Cancellation, Rescheduling and Refunds</h2>
              <p>
                Cancellation, rescheduling and refund terms may vary depending on the specific
                healthcare service and the relevant healthcare provider.
              </p>
              <p>
                Where applicable, the cancellation, rescheduling or refund conditions will be
                communicated at the time of booking.
              </p>
              <p>
                Certain services may not be refundable after the service has been initiated,
                completed or where a third-party provider has incurred a charge.
              </p>
              <p>
                Where a refund is approved, the refund may be processed through the applicable
                payment method and within the applicable processing period.
              </p>
              <p>
                For Organization-sponsored subscriptions, cancellation and commercial refunds will
                generally be governed by the agreement between Unaffected Healthcare and the
                Organization.
              </p>
            </div>

            <div className="content-section">
              <h2>21. Healthcare Provider Availability</h2>
              <p>Healthcare services are subject to provider and operational availability.</p>
              <p>A healthcare provider may become unavailable due to:</p>
              <ul>
                <li>Appointment capacity;</li>
                <li>Location;</li>
                <li>Holidays;</li>
                <li>Operational limitations;</li>
                <li>Technical issues;</li>
                <li>Changes in partnership arrangements;</li>
                <li>Regulatory requirements; or</li>
                <li>Circumstances beyond Unaffected Healthcare&apos;s reasonable control.</li>
              </ul>
              <p>
                Where reasonably possible, Unaffected Healthcare may provide an alternative
                appointment, provider or service.
              </p>
            </div>

            <div className="content-section">
              <h2>22. User Responsibilities</h2>
              <p>Users agree to:</p>
              <ul>
                <li>Provide accurate information;</li>
                <li>Keep profile information updated;</li>
                <li>
                  Provide healthcare professionals with relevant and accurate medical information;
                </li>
                <li>
                  Inform healthcare professionals about relevant allergies, medications and medical
                  history;
                </li>
                <li>Follow appropriate medical advice;</li>
                <li>Attend scheduled appointments on time;</li>
                <li>Use the Platform only for lawful purposes;</li>
                <li>Respect healthcare professionals and service partners;</li>
                <li>Not misuse healthcare benefits;</li>
                <li>Not create fraudulent accounts;</li>
                <li>Not submit false prescriptions, reports or documents;</li>
                <li>Not impersonate another person; and</li>
                <li>
                  Not attempt to interfere with the security or operation of the Platform.
                </li>
              </ul>
            </div>

            <div className="content-section">
              <h2>23. Prohibited Activities</h2>
              <p>Users shall not:</p>
              <ol>
                <li>Use the Platform for unlawful purposes.</li>
                <li>
                  Attempt to obtain unauthorized access to the Platform or another User&apos;s
                  account.
                </li>
                <li>Share or misuse another person&apos;s account.</li>
                <li>Submit false, fraudulent or misleading information.</li>
                <li>Upload malicious software, viruses or harmful code.</li>
                <li>Reverse engineer or attempt to extract source code from the Platform.</li>
                <li>Scrape or systematically collect Platform data without authorization.</li>
                <li>Circumvent Platform security mechanisms.</li>
                <li>Abuse promotional or complimentary benefits.</li>
                <li>
                  Use the Platform to harass, threaten or abuse healthcare professionals, employees
                  or service partners.
                </li>
                <li>Use the Platform for fraudulent medical or financial activities.</li>
                <li>Interfere with the operation or availability of the Platform.</li>
              </ol>
              <p>
                Unaffected Healthcare may suspend or restrict access where it reasonably believes
                that a User has violated these Terms.
              </p>
            </div>

            <div className="content-section">
              <h2>24. Intellectual Property</h2>
              <p>
                All intellectual property relating to the Platform, unless otherwise stated, belongs
                to or is licensed to Unaffected Healthcare.
              </p>
              <p>This includes:</p>
              <ul>
                <li>The Unaffected Healthcare name and brand;</li>
                <li>Logos and trademarks;</li>
                <li>Website design;</li>
                <li>Software;</li>
                <li>User interface;</li>
                <li>Text;</li>
                <li>Graphics;</li>
                <li>Images;</li>
                <li>Videos;</li>
                <li>Databases;</li>
                <li>Content;</li>
                <li>Reports and materials; and</li>
                <li>Other proprietary materials.</li>
              </ul>
              <p>
                Users may access and use such content only for their personal, non-commercial use in
                connection with the authorized use of the Platform.
              </p>
              <p>
                No User may reproduce, copy, modify, distribute, sell, publish, transmit, license or
                commercially exploit Platform content without prior written permission.
              </p>
            </div>

            <div className="content-section">
              <h2>25. Third-Party Links and Services</h2>
              <p>
                The Platform may contain links, integrations or references to third-party websites,
                applications or services.
              </p>
              <p>
                These may include healthcare providers, laboratories, pharmacies, payment service
                providers and other partners.
              </p>
              <p>
                Third-party services may be governed by their own terms and privacy policies.
              </p>
              <p>
                Unaffected Healthcare is not responsible for third-party websites, applications or
                services that are independently operated by third parties, except to the extent
                required by applicable law.
              </p>
            </div>

            <div className="content-section">
              <h2>26. Platform Availability</h2>
              <p>
                Unaffected Healthcare will make reasonable efforts to maintain the availability and
                functionality of the Platform.
              </p>
              <p>However, continuous or uninterrupted availability cannot be guaranteed.</p>
              <p>The Platform may temporarily become unavailable due to:</p>
              <ul>
                <li>Maintenance;</li>
                <li>Updates;</li>
                <li>Technical failures;</li>
                <li>Network problems;</li>
                <li>Cybersecurity incidents;</li>
                <li>Third-party service failures;</li>
                <li>Infrastructure failures;</li>
                <li>Government restrictions; or</li>
                <li>Other circumstances beyond our reasonable control.</li>
              </ul>
            </div>

            <div className="content-section">
              <h2>27. Medical Outcome Disclaimer</h2>
              <p>Healthcare outcomes vary between individuals.</p>
              <p>Unaffected Healthcare does not guarantee:</p>
              <ul>
                <li>A particular medical outcome;</li>
                <li>Recovery from a medical condition;</li>
                <li>A specific treatment result;</li>
                <li>A specific diagnostic result;</li>
                <li>A specific response to medication;</li>
                <li>A specific waiting time; or</li>
                <li>Any particular health improvement.</li>
              </ul>
              <p>
                Nothing on the Platform should be interpreted as a guarantee of a medical outcome.
              </p>
            </div>

            <div className="content-section">
              <h2>28. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by applicable law, Unaffected Healthcare shall not be
                liable for losses arising from matters outside its reasonable control or from
                services independently provided by third-party healthcare providers, including:
              </p>
              <ul>
                <li>Medical decisions made by healthcare professionals;</li>
                <li>Diagnosis or treatment decisions made by healthcare professionals;</li>
                <li>Medical outcomes;</li>
                <li>Errors or omissions by third-party healthcare providers;</li>
                <li>Diagnostic results generated by third-party laboratories;</li>
                <li>Medicine availability;</li>
                <li>Pharmacy delivery delays;</li>
                <li>Third-party provider cancellations;</li>
                <li>Internet or telecommunications failures;</li>
                <li>Delays caused by third parties; or</li>
                <li>Information inaccurately provided by a User.</li>
              </ul>
              <p>
                Nothing in these Terms is intended to exclude or limit any liability that cannot
                lawfully be excluded or limited under applicable law.
              </p>
              <p>
                Unaffected Healthcare does not exclude liability for matters that cannot legally be
                excluded under applicable Indian law.
              </p>
            </div>

            <div className="content-section">
              <h2>29. Indemnification</h2>
              <p>
                To the extent permitted by applicable law, you agree to indemnify and hold harmless
                Unaffected Healthcare LLP, its partners, employees, officers, representatives and
                service partners from claims, losses, liabilities, damages, costs and expenses
                arising from:
              </p>
              <ul>
                <li>Your breach of these Terms;</li>
                <li>Your misuse of the Platform;</li>
                <li>Fraudulent or inaccurate information provided by you;</li>
                <li>Your unlawful use of the Platform;</li>
                <li>Your violation of another person&apos;s rights; or</li>
                <li>Your violation of applicable law.</li>
              </ul>
              <p>
                This clause shall not apply to the extent that a claim arises solely from the
                negligence, wilful misconduct or unlawful conduct of Unaffected Healthcare.
              </p>
            </div>

            <div className="content-section">
              <h2>30. Suspension and Termination</h2>
              <p>
                Unaffected Healthcare may suspend, restrict or terminate access to the Platform
                where:
              </p>
              <ul>
                <li>These Terms have been violated;</li>
                <li>Fraudulent or abusive activity is suspected;</li>
                <li>Unauthorized access is attempted;</li>
                <li>The Platform is being misused;</li>
                <li>Continued access creates a security or legal risk;</li>
                <li>
                  The User is no longer eligible under the applicable Organization program; or
                </li>
                <li>Required by applicable law.</li>
              </ul>
              <p>
                Where an employee&apos;s employment or eligibility with the sponsoring Organization
                ends, access to Organization-sponsored benefits may be modified, suspended or
                terminated in accordance with the applicable Organization agreement.
              </p>
            </div>

            <div className="content-section">
              <h2>31. Changes to Services</h2>
              <p>Unaffected Healthcare may from time to time:</p>
              <ul>
                <li>Add new services;</li>
                <li>Modify existing services;</li>
                <li>Change healthcare partners;</li>
                <li>Remove services;</li>
                <li>Change service availability; or</li>
                <li>Introduce new plans or programs.</li>
              </ul>
              <p>
                Changes to an Organization&apos;s contracted benefits will be handled in accordance
                with the applicable agreement between the Organization and Unaffected Healthcare.
              </p>
            </div>

            <div className="content-section">
              <h2>32. Changes to These Terms</h2>
              <p>Unaffected Healthcare may update these Terms from time to time.</p>
              <p>
                The updated Terms will be published on the Platform with a revised &quot;Last
                Updated&quot; date.
              </p>
              <p>
                Where required by applicable law, material changes may be communicated through
                appropriate means.
              </p>
              <p>
                Your continued use of the Platform after updated Terms become effective constitutes
                acceptance of the revised Terms, to the extent permitted by applicable law.
              </p>
            </div>

            <div className="content-section">
              <h2>33. Data Protection and Privacy</h2>
              <p>
                Unaffected Healthcare will process personal data in accordance with applicable Indian
                data-protection and privacy laws.
              </p>
              <p>The Platform may process personal information for purposes including:</p>
              <ul>
                <li>Account creation;</li>
                <li>Identity and eligibility verification;</li>
                <li>Healthcare service delivery;</li>
                <li>Appointment management;</li>
                <li>Communication;</li>
                <li>Customer support;</li>
                <li>Payment processing where applicable;</li>
                <li>Service improvement;</li>
                <li>Security and fraud prevention;</li>
                <li>Legal and regulatory compliance; and</li>
                <li>Other purposes described in the Privacy Policy.</li>
              </ul>
              <p>The Privacy Policy forms an integral part of these Terms.</p>
              <p>Users are encouraged to review the Privacy Policy before using the Platform.</p>
              <p>
                Unaffected Healthcare will implement appropriate measures for protecting personal
                data as required by applicable law.
              </p>
              <p>
                India&apos;s Digital Personal Data Protection framework includes the Digital Personal
                Data Protection Act, 2023 and the Digital Personal Data Protection Rules, 2025. The
                Government&apos;s published implementation framework provides for phased commencement
                of specified provisions.
              </p>
            </div>

            <div className="content-section">
              <h2>34. Electronic Communications</h2>
              <p>
                By using the Platform, you agree that Unaffected Healthcare may communicate with you
                electronically through channels such as:
              </p>
              <ul>
                <li>Email;</li>
                <li>SMS;</li>
                <li>WhatsApp;</li>
                <li>Notifications;</li>
                <li>Telephone; or</li>
                <li>Other communication methods associated with the services.</li>
              </ul>
              <p>These communications may include:</p>
              <ul>
                <li>Account activation;</li>
                <li>Appointment confirmations;</li>
                <li>Healthcare service updates;</li>
                <li>Reports or service notifications;</li>
                <li>Transactional communications;</li>
                <li>Support communications;</li>
                <li>Important Platform notices; and</li>
                <li>Other service-related communications.</li>
              </ul>
              <p>
                Marketing communications, where applicable, will be subject to applicable consent and
                opt-out requirements.
              </p>
            </div>

            <div className="content-section">
              <h2>35. Force Majeure</h2>
              <p>
                Unaffected Healthcare shall not be responsible for failure or delay in performing its
                obligations where such failure or delay results from circumstances beyond its
                reasonable control, including:
              </p>
              <ul>
                <li>Natural disasters;</li>
                <li>Epidemics or pandemics;</li>
                <li>Government restrictions;</li>
                <li>War;</li>
                <li>Civil unrest;</li>
                <li>Strikes;</li>
                <li>Internet or telecommunications failures;</li>
                <li>Cybersecurity incidents;</li>
                <li>Infrastructure failures;</li>
                <li>Power failures;</li>
                <li>Healthcare provider disruptions; or</li>
                <li>Other unforeseen circumstances beyond reasonable control.</li>
              </ul>
            </div>

            <div className="content-section">
              <h2>36. Severability</h2>
              <p>
                If any provision of these Terms is determined to be unlawful, invalid or
                unenforceable, that provision shall be modified or interpreted to the minimum extent
                necessary to make it enforceable, where permitted by law.
              </p>
              <p>The remaining provisions shall continue to remain in full force and effect.</p>
            </div>

            <div className="content-section">
              <h2>37. Waiver</h2>
              <p>
                Failure by Unaffected Healthcare to enforce any provision of these Terms shall not
                constitute a waiver of its right to enforce that provision or any other provision in
                the future.
              </p>
            </div>

            <div className="content-section">
              <h2>38. Entire Agreement</h2>
              <p>
                These Terms, together with the Privacy Policy and any applicable service-specific
                terms, constitute the terms governing the User&apos;s use of the Platform.
              </p>
              <p>
                For Organizations, these Terms may be supplemented or superseded by a separate
                written commercial agreement, proposal, order form, subscription agreement or other
                contract entered into between the Organization and Unaffected Healthcare.
              </p>
            </div>

            <div className="content-section">
              <h2>39. Governing Law</h2>
              <p>
                These Terms shall be governed by and interpreted in accordance with the laws of the
                Republic of India.
              </p>
            </div>

            <div className="content-section">
              <h2>40. Jurisdiction</h2>
              <p>
                Subject to applicable law, courts having competent jurisdiction in Gautam Buddha
                Nagar, Uttar Pradesh, shall have jurisdiction over disputes arising out of or
                relating to these Terms or the use of the Platform.
              </p>
              <p>
                Nothing in this clause shall restrict any mandatory statutory right or jurisdiction
                that cannot lawfully be excluded under applicable law.
              </p>
            </div>

            <div className="content-section">
              <h2>41. Grievance Redressal</h2>
              <p>
                For questions, complaints or grievances relating to the Platform or services, you may
                contact Unaffected Healthcare at:
              </p>
              <p>
                <strong>Unaffected Healthcare LLP</strong>
                <br />
                Address: {OFFICE_ADDRESS}
                <br />
                Website:{' '}
                <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                  {WEBSITE_URL}
                </a>
                <br />
                Email: {SUPPORT_EMAIL}
              </p>
              <p>
                We will endeavour to acknowledge and address grievances within the timelines
                prescribed under applicable law.
              </p>
            </div>

            <div className="content-section">
              <h2>42. Contact Us</h2>
              <p>For questions regarding these Terms &amp; Conditions, please contact:</p>
              <p>
                <strong>Unaffected Healthcare LLP</strong>
                <br />
                B-128, B Block, Sector 2,
                <br />
                Noida, Uttar Pradesh – 201301
                <br />
                Email: {SUPPORT_EMAIL}
                <br />
                Website:{' '}
                <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                  {WEBSITE_URL}
                </a>
              </p>
            </div>

            <div className="content-section">
              <h2>43. Acceptance</h2>
              <p>
                By accessing or using the Unaffected Healthcare Platform, you acknowledge that:
              </p>
              <ol>
                <li>You have read these Terms &amp; Conditions;</li>
                <li>You understand the provisions contained herein;</li>
                <li>You agree to comply with these Terms; and</li>
                <li>You agree to use the Platform in accordance with applicable law.</li>
              </ol>
              <p>
                If you do not agree to these Terms, please discontinue your use of the Platform.
              </p>
              <p>© Unaffected Healthcare LLP. All rights reserved.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
